/**
 * Prototype motion that CSS keyframes cannot do (ledger R7/R8/R12/R17), done with WAAPI: the page leave, the chip pop,
 * the nav icon spin and the Bio leaf burst. Every function does nothing under prefers-reduced-motion.
 */

import { bewegungOk } from '../../shared/lib/dialogAbgang.ts'

export { bewegungOk, dialogVerlassen } from '../../shared/lib/dialogAbgang.ts'
export type { DialogAbgang } from '../../shared/lib/dialogAbgang.ts'

/** The prototype's back.out overshoot. */
export const UEBERSCHWUNG = 'cubic-bezier(0.34, 1.56, 0.64, 1)'

/** Prototype barcode acceptance: scale 0, rotate -12° → normal, 350 ms, power2.out. */
export function barcodeBestaetigen(el: Element | null): () => void {
  if (!el || !bewegungOk() || typeof el.animate !== 'function') return () => {}
  const lauf = el.animate(
    [{ transform: 'scale(0) rotate(-12deg)' }, { transform: 'scale(1) rotate(0deg)' }],
    { duration: 350, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)', fill: 'backwards' },
  )
  return () => lauf.cancel()
}

/** Kassenbuch's calm step reveal: y 10 → 0, opacity 0 → 1, 320 ms, power2.out, stagger 35 ms. */
export function schritteEinblenden(kinder: Element[]): () => void {
  if (!bewegungOk()) return () => {}
  const laeufe = kinder.flatMap((el, i) => typeof el.animate !== 'function' ? [] : [el.animate(
    [{ transform: 'translateY(10px)', opacity: 0 }, { transform: 'none', opacity: 1 }],
    { duration: 320, delay: i * 35, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)', fill: 'backwards' },
  )])
  return () => laeufe.forEach(a => a.cancel())
}

/** Dashboard greeting by hour: before 11 morning, before 17 day, then evening. */
export const tageszeit = (stunde: number): 'morgen' | 'tag' | 'abend' => (stunde < 11 ? 'morgen' : stunde < 17 ? 'tag' : 'abend')

/** Prototype go(): the old page flies up, tilts and fades out (y -40, rotateX 12, 250 ms). Resolves when it is gone. */
export function seiteVerlassen(kinder: Element[]): Promise<void> {
  if (!bewegungOk() || kinder.length === 0 || typeof kinder[0].animate !== 'function') return Promise.resolve()
  const laeufe = kinder.map((el, i) => el.animate(
    [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'perspective(1200px) translateY(-40px) rotateX(12deg) scale(0.97)' }],
    { duration: 250, delay: i * 20, easing: 'cubic-bezier(0.55, 0.06, 0.68, 0.19)', fill: 'forwards' },
  ))
  return Promise.all(laeufe.map((a) => a.finished)).then(() => undefined, () => undefined)
}

/** Prototype setScope(): the tapped chip pops (scale 1.25, rotate -6deg, springs back). */
export function poppen(el: Element | null) {
  if (!el || !bewegungOk() || typeof el.animate !== 'function') return
  el.animate([{ transform: 'scale(1.25) rotate(-6deg)' }, { transform: 'none' }], { duration: 500, easing: UEBERSCHWUNG })
}

/** Prototype identify(): the reader core flies in from the left after a chip read (x -90, rotate -30deg, scale 0.7, back.out(2), 0.5 s). Resolves when it has landed. */
export function kernEinfliegen(el: Element | null): Promise<void> {
  if (!el || !bewegungOk() || typeof el.animate !== 'function') return Promise.resolve()
  const a = el.animate([{ transform: 'translateX(-90px) rotate(-30deg) scale(0.7)' }, { transform: 'none' }], { duration: 500, easing: 'cubic-bezier(0.3, 1.9, 0.6, 1)' })
  return a.finished.then(() => undefined, () => undefined)
}

/** Prototype swap(): the dialog body fades out (0.15 s, power1.in) BEFORE the step changes. It stays hidden (fill forwards) until `abbrechen()` runs after the new step is in place. */
export function koerperAusblenden(el: Element | null): { fertig: Promise<void>; abbrechen: () => void } {
  if (!el || !bewegungOk() || typeof el.animate !== 'function') return { fertig: Promise.resolve(), abbrechen: () => {} }
  const a = el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: 'cubic-bezier(0.55, 0.085, 0.68, 0.53)', fill: 'forwards' })
  return { fertig: a.finished.then(() => undefined, () => undefined), abbrechen: () => a.cancel() }
}

/** Prototype nav icon: the active icon spins in (-200deg, scale 0.4, 700 ms). */
export function eindrehen(el: Element | null) {
  if (!el || !bewegungOk() || typeof el.animate !== 'function') return
  el.animate([{ transform: 'rotate(-200deg) scale(0.4)' }, { transform: 'none' }], { duration: 700, easing: 'cubic-bezier(0.3, 1.9, 0.6, 1)' })
}

export interface BlattBahn { dx: number; dy: number; skala: number; drehung: number; dauer: number }
const zwischen = (z: number, a: number, b: number) => a + z * (b - a)

/** Pure: one leaf path from random numbers in [0,1) (prototype leafBurst: x ±320, y -260..120, scale 0.6..1.6, rotate ±540, 1.2..2.2 s). */
export function blattBahn(z: () => number): BlattBahn {
  return { dx: zwischen(z(), -320, 320), dy: zwischen(z(), -260, 120), skala: zwischen(z(), 0.6, 1.6), drehung: zwischen(z(), -540, 540), dauer: zwischen(z(), 1200, 2200) }
}

export const BLATT_ANZAHL = 30

/** Prototype leafBurst: 30 "eco" icons fly out of the middle of `ursprung`, turn and fade, then remove themselves. */
export function blaetterFliegen(ursprung: Element | null, z: () => number = Math.random) {
  if (!ursprung || !bewegungOk()) return
  const r = ursprung.getBoundingClientRect()
  const cx = r.left + r.width / 2
  const cy = r.top + r.height / 2
  for (let i = 0; i < BLATT_ANZAHL; i++) {
    const b = blattBahn(z)
    const el = document.createElement('span')
    el.className = 'material-symbols-outlined text-chart-bio'
    el.setAttribute('aria-hidden', 'true')
    el.textContent = 'eco'
    Object.assign(el.style, { position: 'fixed', left: `${cx}px`, top: `${cy}px`, zIndex: '70', pointerEvents: 'none', fontSize: '28px', fontVariationSettings: "'FILL' 1" })
    document.body.appendChild(el)
    const a = el.animate(
      [{ transform: 'translate(0, 0) scale(0.2) rotate(0deg)', opacity: 1 }, { transform: `translate(${b.dx}px, ${b.dy}px) scale(${b.skala}) rotate(${b.drehung}deg)`, opacity: 0 }],
      { duration: b.dauer, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)', fill: 'forwards' },
    )
    a.finished.then(() => el.remove(), () => el.remove())
  }
}

let einlaufGespielt = false

/** True once per browser session (module flag + sessionStorage): the first-load intro must not replay on a route change or a remount. */
export function einlaufFaellig(): boolean {
  if (einlaufGespielt) return false
  einlaufGespielt = true
  try {
    if (window.sessionStorage.getItem('sp-einlauf') === '1') return false
    window.sessionStorage.setItem('sp-einlauf', '1')
  } catch { /* storage blocked: the module flag still limits it to once per page load */ }
  return true
}

/**
 * Prototype first load: the top bar drops in (y -80, rotateX -40, 900 ms), the sidebar links fly in one after another
 * (x -60, rotate -8, 600 ms, stagger 60, delay 200) and the bottom bar slides up (y 100, 700 ms, delay 300).
 * Targets carry data-sp-intro="top" | "nav" | "bottom". Does nothing under prefers-reduced-motion.
 */
export function einlaufSpielen(wurzel: ParentNode | null) {
  if (!wurzel || !bewegungOk()) return
  const lauf = (el: Element, von: Keyframe, optionen: KeyframeAnimationOptions) => {
    if (typeof el.animate === 'function') el.animate([von, { opacity: 1, transform: 'none' }], { fill: 'backwards', ...optionen })
  }
  wurzel.querySelectorAll('[data-sp-intro="top"]').forEach((el) =>
    lauf(el, { opacity: 0, transform: 'perspective(1200px) translateY(-80px) rotateX(-40deg)' }, { duration: 900, easing: 'cubic-bezier(0.34, 1.4, 0.64, 1)' }))
  wurzel.querySelectorAll('[data-sp-intro="nav"]').forEach((el, i) =>
    lauf(el, { opacity: 0, transform: 'translateX(-60px) rotate(-8deg)' }, { duration: 600, delay: 200 + i * 60, easing: 'cubic-bezier(0.34, 2, 0.64, 1)' }))
  wurzel.querySelectorAll('[data-sp-intro="bottom"]').forEach((el) =>
    lauf(el, { transform: 'translateY(100px)' }, { duration: 700, delay: 300, easing: 'cubic-bezier(0.34, 1.5, 0.64, 1)' }))
}

/** Prototype form error: the dialog shakes (x -12 to 0, 500 ms). */
export function schuetteln(el: Element | null) {
  if (!el || !bewegungOk() || typeof el.animate !== 'function') return
  // One-sided settle (spec B10): x -12 to 0 over 0.5 s, GSAP power2.out (measured -6.9 px at 70 ms); NOT elastic, never crosses 0.
  el.animate([{ transform: 'translateX(-12px)' }, { transform: 'none' }], { duration: 500, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)' })
}

/**
 * Prototype measureDialog(): the deviation card flips to the edge (rotateY 90, 250 ms), turns green, flips back (250 ms) and the
 * green fades out. The deviation itself stays in the list (its alert is closed by the data, not by the click), so it does not fly away.
 */
export function kippenGruen(el: Element | null): Promise<void> {
  if (!el || !bewegungOk() || typeof el.animate !== 'function') return Promise.resolve()
  const gruen = 'rgba(16, 185, 129, 0.22)'
  const a = el.animate([
    { transform: 'perspective(1200px) rotateY(0deg)', backgroundColor: 'rgba(16, 185, 129, 0)', offset: 0, easing: 'cubic-bezier(0.55, 0.085, 0.68, 0.53)' },
    { transform: 'perspective(1200px) rotateY(90deg)', backgroundColor: gruen, offset: 0.28, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' },
    { transform: 'perspective(1200px) rotateY(0deg)', backgroundColor: gruen, offset: 0.56 },
    { transform: 'perspective(1200px) rotateY(0deg)', backgroundColor: gruen, offset: 0.7 },
    { transform: 'none', backgroundColor: 'rgba(16, 185, 129, 0)', offset: 1 },
  ], { duration: 900 })
  return a.finished.then(() => undefined, () => undefined)
}

/** Logout: the next person on the same tab gets the first-load intro again (the page reload resets the module flag). */
export function einlaufZuruecksetzen() {
  einlaufGespielt = false
  try { window.sessionStorage.removeItem('sp-einlauf') } catch { /* storage blocked: nothing to clear */ }
}
